import ImageRadio from '@/components/ui/radio/ReactHookFormImageRadio';
import React, { useEffect, useMemo, useState } from 'react';

import CoordinateUtils, { TimezoneOption } from '@/utils/coordinate';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import ReactHookFormSelect from '@/components/ui/select/ReactHookFormSelect';
import ReactHookFormToggleSwitch from '@/components/ui/switch/ReactHookFormToogleSwitch';
import useAuthStore from '@/store/auth';
import { useMutationEditSettings } from '@/api/queries/customers';
import toast from 'react-hot-toast';
import { EditAccountSettingsForm } from '@/types/form';
import { User } from '@/types/api';
import { OptionSelect } from '@/components/ui/select';
import { OptionImageRadio } from '@/components/ui/radio/BaseImageRadio';
import { isWebGLSupported, isLowEndDevice } from '@/utils/hardwareDetection';
import { Loader2, RefreshCw } from 'lucide-react';

type Props = {
  footerExt?: React.ReactNode;
};

const schema = Yup.object().shape({
  theme: Yup.string().oneOf(['light', 'dark']).required('Theme is required'),
  map_type: Yup.string()
    .oneOf(['terrain', 'satellite', 'street', 'grayscale'])
    .required('Map Type is required'),
  auto_refresh: Yup.string().required('Auto Refresh is required'),
  coordinate_format: Yup.string().oneOf(['DD', 'DMS']).required('Coordinate Format is required'),
  timezone: Yup.string()
    .matches(/^[+-]?\d+$/, 'Timezone must be a valid number')
    .required('Timezone is required'),
  // draggable_marker: Yup.boolean().required('Draggable Marker is required'),
  click_map: Yup.boolean().required('Click Map is required'),
  show_port_by_system: Yup.boolean().required('Show Port By System is required'),
  map_mode: Yup.string().optional(),
});

const defaultValues = {
  theme: 'light',
  map_type: 'street',
  map_mode: 'webgl',
  auto_refresh: '5',
  coordinate_format: 'DD',
  timezone: '+7',
  draggable_marker: false,
  click_map: false,
  show_port_by_system: false,
};

const autoRefreshOptions: OptionSelect[] = [
  { value: '1', label: '1 Minute' },
  { value: '5', label: '5 Minute' },
  { value: '10', label: '10 Minute' },
  { value: '20', label: '20 Minute' },
  { value: '30', label: '30 Minute' },
  { value: '40', label: '40 Minute' },
  { value: '50', label: '50 Minute' },
  { value: '60', label: '60 Minute' },
];

const optionsTheme: OptionImageRadio[] = [
  {
    value: 'light',
    imageSrc: '/images/light-mode.png',
    label: 'Light',
    classNameImage: 'w-30 h-24',
  },
  {
    value: 'dark',
    imageSrc: '/images/dark-mode.png',
    label: 'Dark',
    classNameImage: 'w-30 h-24',
  },
];

const optiosnMap: OptionImageRadio[] = [
  {
    value: 'terrain',
    imageSrc: '/images/terrain.png',
    label: 'Terrain',
    classNameImage: '!w-20 !h-20',
  },
  {
    value: 'street',
    imageSrc: '/images/street.png',
    label: 'Street',
    classNameImage: '!w-20 !h-20',
  },
  {
    value: 'satellite',
    imageSrc: '/images/sattelite.png',
    label: 'Satellite',
    classNameImage: '!w-20 !h-20',
  },
  {
    value: 'grayscale',
    imageSrc: '/images/grayscale.png',
    label: 'Grayscale',
    classNameImage: '!w-20 !h-20',
  },
];
const coordinateFormatOptions = [
  { value: 'DD', label: 'Decimal Degrees' },
  { value: 'DMS', label: 'Degrees Minutes Seconds' },
];

export default function Appearence({ footerExt }: Props) {
  const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false);

  const timezoneOptions = useMemo(() => CoordinateUtils.generateTimezoneOptions(), []);

  const { setLogin, user } = useAuthStore((state) => state);
  const { isPending, mutateAsync } = useMutationEditSettings();

  const getDefaultData = (user: User) => {
    try {
      const userData = user.settingsObj || (typeof user.settings === 'string' ? JSON.parse(user.settings) : null);

      if (userData) {
        return {
          theme: userData?.theme || 'light',
          map_type: userData?.map_type || 'street',
          map_mode: userData?.map_mode || 'webgl',
          auto_refresh: userData?.auto_refresh || '5',
          coordinate_format: userData?.coordinate_format || 'DD',
          timezone: userData?.timezone || '+7',
          draggable_marker: userData?.draggable_marker || false,
          click_map: userData?.click_map || false,
          show_port_by_system: userData?.show_port_by_system || false,
        };
      } else {
        return defaultValues;
      }
    } catch (e) {
      return defaultValues;
    }
  };

  const {
    control,
    setValue,
    getValues,
    trigger,
    watch,
    reset,
  } = useForm({
    // @ts-ignore
    defaultValues: user?.settings ? getDefaultData(user) : defaultValues,
    mode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (user?.settings) {
      const data = getDefaultData(user);
      if (typeof window !== 'undefined' && !isWebGLSupported()) {
        data.map_mode = 'leaflet';
      }
      reset(data);
    }
  }, [user?.settings, reset]);

  const [deviceCapability, setDeviceCapability] = useState<{ webglSupported: boolean; isLowEnd: boolean }>({
    webglSupported: true,
    isLowEnd: false,
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const webgl = isWebGLSupported();
      const lowEnd = isLowEndDevice();
      setDeviceCapability({ webglSupported: webgl, isLowEnd: lowEnd });
      if (!webgl) {
        setValue('map_mode', 'leaflet');
      }
    }
  }, [setValue]);

  const handleSaveAccount = async () => {
    try {
      const validate = await trigger();
      if (!validate) {
        return;
      }

      const data: EditAccountSettingsForm = getValues();
      const response = await mutateAsync({ params: data });
      if (response) {
        if (typeof response.settings === 'string' && !response.settingsObj) {
          try {
            response.settingsObj = JSON.parse(response.settings);
          } catch {}
        }
        setLogin(response);
        if (data.map_mode) {
          const engineMode = data.map_mode === 'leaflet' ? 'maps-leaflet' : 'maps-gl';
          if (typeof window !== 'undefined') {
            localStorage.setItem('map_mode', engineMode);
          }
          useAuthStore.getState().setMapMode(engineMode, false);
        }

        // Apply theme immediately to DOM
        if (data.theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }

        toast.success('Settings saved successfully!');
        setShowRefreshModal(true);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Could not save settings';
      toast.error(`Save failed: ${message}`);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Interface Theme</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Select or customize your UI Theme</p>
        </div>
        <div className="">
          <ImageRadio name="theme" options={optionsTheme} control={control} disabled={isPending} />
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Default Map Type</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Select your default map when first time access</p>
        </div>
        <div className="">
          <ImageRadio name="map_type" options={optiosnMap} control={control} disabled={isPending} />
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Map Engine (Map Mode)</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Web GL (smoother) or Leaflet (faster)</p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-1.5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!deviceCapability.webglSupported || isPending}
              onClick={() => {
                if (!deviceCapability.webglSupported) {
                  toast.error('Perangkat/browser ini tidak mendukung WebGL. Otomatis menggunakan Leaflet.');
                  return;
                }
                setValue('map_mode', 'webgl');
              }}
              className={`px-4 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all flex items-center gap-2 ${
                !deviceCapability.webglSupported
                  ? 'opacity-60 border-dashed border-slate-300 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/40 cursor-not-allowed text-slate-400'
                  : (watch('map_mode') === 'webgl' || !watch('map_mode'))
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 cursor-pointer'
                  : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300 cursor-pointer'
              }`}
            >
              <span>Web GL</span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                  !deviceCapability.webglSupported
                    ? 'bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400'
                    : 'bg-blue-500 text-white'
                }`}
              >
                {!deviceCapability.webglSupported ? 'Unsupported' : 'Smoother'}
              </span>
            </button>
            <button
              type="button"
              disabled={isPending}
              onClick={() => setValue('map_mode', 'leaflet')}
              className={`px-4 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                watch('map_mode') === 'leaflet'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <span>Leaflet</span>
              <span className="text-[10px] bg-slate-200 dark:bg-gray-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded font-bold uppercase">
                Faster
              </span>
              {deviceCapability.isLowEnd && (
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold ml-1">
                  (Rekomendasi)
                </span>
              )}
            </button>
          </div>
          {!deviceCapability.webglSupported && (
            <span className="text-[11px] text-rose-500 dark:text-rose-400 font-medium">
              Browser/perangkat tidak mendukung WebGL. Sistem otomatis menggunakan Leaflet.
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Auto Refresh</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Set your refresh page</p>
        </div>
        <div className="w-44">
          <ReactHookFormSelect
            name="auto_refresh"
            control={control}
            options={autoRefreshOptions}
            disabled={isPending}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Coordinate Format</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Choose your coordinate format Decimal Degree or DMS</p>
        </div>
        <div className="w-44">
          <ReactHookFormSelect
            name="coordinate_format"
            control={control}
            options={coordinateFormatOptions}
            disabled={isPending}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Timezone Format</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Choose your location timezone</p>
        </div>
        <div className="w-44">
          <ReactHookFormSelect
            name="timezone"
            control={control}
            options={timezoneOptions}
            disabled={isPending}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Show Port By System</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            By choosing this setting every port by system will show on the map load
          </p>
        </div>
        <div className="">
          <ReactHookFormToggleSwitch
            name="show_port_by_system"
            control={control}
            disabled={isPending}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-6 dark:text-white">
        <div className="w-full md:w-64">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Click Map</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Activate Click Map will trigger pop up to get latitude and longitude
          </p>
        </div>
        <div className="">
          <ReactHookFormToggleSwitch name="click_map" control={control} disabled={isPending} />
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-3 pb-28 md:pb-2 flex justify-end">
        <button
          type="button"
          onClick={() => handleSaveAccount()}
          disabled={isPending}
          className="w-full md:w-auto md:min-w-[160px] py-3 px-6 rounded-xl font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] disabled:opacity-60 transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
        {footerExt}
      </div>

      {/* Refresh Confirmation Modal (Mandatory, Non-Cancelable) */}
      {showRefreshModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[99999999] flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-2xl border border-slate-100 dark:border-gray-700 space-y-4 text-center animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto shadow-inner">
              <RefreshCw className="w-6 h-6 animate-spin" style={{ animationDuration: '3s' }} />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#021c32] dark:text-white">Refresh Page</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                Settings have been saved. Refresh the page to apply changes to the map engine and system preferences.
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.location.reload();
                  }
                }}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-all shadow-md shadow-blue-500/25 flex items-center justify-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
